// src/pages/TrainRoute.jsx
import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';

const TYPE_LABELS = { Ex: 'Express', IC: 'Intercity', Ml: 'Mail', Com: 'Commuter' };
const TYPE_COLORS = {
    Ex: { bg: 'rgba(251,191,36,.12)', text: '#fbbf24', border: 'rgba(251,191,36,.3)' },
    IC: { bg: 'rgba(99,102,241,.12)', text: '#818cf8', border: 'rgba(99,102,241,.3)' },
    Ml: { bg: 'rgba(34,197,94,.12)', text: '#4ade80', border: 'rgba(34,197,94,.3)' },
    Com: { bg: 'rgba(251,146,60,.12)', text: '#fb923c', border: 'rgba(251,146,60,.3)' },
};

const s = {
    page: { minHeight: '100vh', background: '#0f1117', fontFamily: "'DM Sans', sans-serif", display: 'flex', flexDirection: 'column' },
    topbar: {
        background: '#0f1117', borderBottom: '1px solid #1e2433',
        padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16,
        position: 'sticky', top: 0, zIndex: 100,
    },
    backBtn: {
        padding: '8px 16px', background: '#1e2433', border: '1px solid #2a3348',
        borderRadius: 8, color: '#94a3b8', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6,
    },
    trainInfo: { flex: 1 },
    trainTitle: { color: '#f1f5f9', fontSize: 18, fontWeight: 700, letterSpacing: '-.3px' },
    trainMeta: { color: '#475569', fontSize: 12, marginTop: 2 },
    badge: { padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, border: '1px solid' },
    body: { display: 'flex', flex: 1, minHeight: 0 },
    sidebar: {
        width: 340, background: '#0d1117', borderRight: '1px solid #1e2433',
        overflowY: 'auto', flexShrink: 0,
    },
    sidebarHeader: { padding: '20px 20px 12px', borderBottom: '1px solid #1e2433', position: 'sticky', top: 0, background: '#0d1117', zIndex: 5 },
    sidebarTitle: { color: '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' },
    stationList: { padding: '12px 0' },
    stationItem: { display: 'flex', gap: 0, padding: '0 20px', position: 'relative' },
    stationLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: 14, width: 20 },
    stationDot: { width: 12, height: 12, borderRadius: '50%', background: '#3b82f6', border: '2px solid #0f1117', flexShrink: 0, zIndex: 1 },
    stationDotFirst: { background: '#22c55e', width: 14, height: 14 },
    stationDotLast: { background: '#6366f1', width: 14, height: 14 },
    stationLine: { width: 2, flex: 1, background: 'linear-gradient(180deg, #3b82f6 0%, #6366f1 100%)', minHeight: 32, margin: '2px 0' },
    stationRight: { flex: 1, paddingBottom: 20 },
    stationCity: { color: '#e2e8f0', fontSize: 14, fontWeight: 600, marginBottom: 4 },
    stationCityFirst: { color: '#22c55e' },
    stationCityLast: { color: '#818cf8' },
    stationMeta: { display: 'flex', gap: 12, flexWrap: 'wrap' },
    metaChip: { display: 'flex', gap: 4, alignItems: 'center', color: '#475569', fontSize: 11 },
    metaVal: { color: '#94a3b8', fontFamily: "'DM Mono', monospace", fontWeight: 600 },
    mapArea: { flex: 1, position: 'relative', background: '#1a1f2e' },
    mapContainer: { width: '100%', height: '100%', minHeight: 500 },
    noRoute: {
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', color: '#475569',
    },
    noRouteIcon: { fontSize: 48, marginBottom: 12 },
    noRouteText: { fontSize: 14, fontWeight: 600 },
    noRouteSub: { fontSize: 12, marginTop: 6, color: '#374151' },
    simpleRoute: { padding: 24 },
    simpleCard: {
        background: '#151a26', border: '1px solid #1e2433', borderRadius: 12,
        padding: 20, display: 'flex', alignItems: 'center', gap: 0,
    },
    simpleStation: { flex: 1, textAlign: 'center' },
    simpleStationName: { color: '#f1f5f9', fontSize: 16, fontWeight: 700 },
    simpleStationLabel: { color: '#475569', fontSize: 11, marginTop: 4, textTransform: 'uppercase', letterSpacing: '.5px' },
    simpleTime: { color: '#3b82f6', fontFamily: "'DM Mono', monospace", fontSize: 18, fontWeight: 700, marginTop: 6 },
    simpleLine: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
    simpleArrow: { color: '#3b82f6', fontSize: 28 },
    simpleDuration: { color: '#475569', fontSize: 11 },
    infoStrip: {
        padding: '12px 20px', borderTop: '1px solid #1e2433',
        display: 'flex', gap: 20, flexWrap: 'wrap',
    },
    infoItem: { display: 'flex', gap: 8, alignItems: 'center', color: '#64748b', fontSize: 12 },
    infoVal: { color: '#94a3b8', fontWeight: 600 },
};

function StationList({ stations }) {
    return (
        <div style={s.stationList}>
            {stations.map((st, i) => {
                const isFirst = i === 0;
                const isLast = i === stations.length - 1;
                return (
                    <div key={i} style={s.stationItem}>
                        <div style={s.stationLeft}>
                            <div style={{ ...s.stationDot, ...(isFirst ? s.stationDotFirst : isLast ? s.stationDotLast : {}) }} />
                            {!isLast && <div style={s.stationLine} />}
                        </div>
                        <div style={s.stationRight}>
                            <div style={{ ...s.stationCity, ...(isFirst ? s.stationCityFirst : isLast ? s.stationCityLast : {}) }}>
                                {st.city?.replace(/_/g, ' ')}
                                {isFirst && <span style={{ marginLeft: 8, fontSize: 10, color: '#22c55e', fontWeight: 600 }}>● শুরু</span>}
                                {isLast && <span style={{ marginLeft: 8, fontSize: 10, color: '#818cf8', fontWeight: 600 }}>● শেষ</span>}
                            </div>
                            <div style={s.stationMeta}>
                                {st.arrival_time && (
                                    <div style={s.metaChip}>🕐 পৌঁছায় <span style={s.metaVal}>{st.arrival_time}</span></div>
                                )}
                                {st.departure_time && (
                                    <div style={s.metaChip}>🚀 ছাড়ে <span style={s.metaVal}>{st.departure_time}</span></div>
                                )}
                                {st.halt && st.halt !== '---' && st.halt !== null && (
                                    <div style={s.metaChip}>⏱ halt <span style={s.metaVal}>{st.halt} min</span></div>
                                )}
                                {st.duration && st.duration !== '---' && st.duration !== null && (
                                    <div style={s.metaChip}>⬆ <span style={s.metaVal}>{st.duration}</span></div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function OSMMap({ stations }) {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);

    useEffect(() => {
        if (!mapRef.current || stations.length === 0) return;

        // Load Leaflet dynamically
        const linkEl = document.createElement('link');
        linkEl.rel = 'stylesheet';
        linkEl.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(linkEl);

        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => {
            const L = window.L;
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }

            const validStations = stations.filter(st => st.lat && st.lng);
            if (validStations.length === 0) return;

            const map = L.map(mapRef.current, { zoomControl: true, attributionControl: true });
            mapInstanceRef.current = map;

            // Dark tile layer
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '© OpenStreetMap contributors',
                maxZoom: 19,
            }).addTo(map);

            const latlngs = validStations.map(st => [st.lat, st.lng]);

            // Draw route polyline
            L.polyline(latlngs, {
                color: '#3b82f6', weight: 3, opacity: .8,
                dashArray: null,
            }).addTo(map);

            // Add markers
            validStations.forEach((st, i) => {
                const isFirst = i === 0;
                const isLast = i === validStations.length - 1;

                const color = isFirst ? '#22c55e' : isLast ? '#6366f1' : '#3b82f6';
                const size = isFirst || isLast ? 14 : 10;

                const icon = L.divIcon({
                    className: '',
                    html: `<div style="
            width:${size}px;height:${size}px;border-radius:50%;
            background:${color};border:2px solid #0f1117;
            box-shadow:0 0 8px ${color}88;
          "></div>`,
                    iconSize: [size, size],
                    iconAnchor: [size / 2, size / 2],
                });

                const marker = L.marker([st.lat, st.lng], { icon }).addTo(map);

                const popupContent = `
          <div style="background:#1e2433;border:1px solid #2a3348;border-radius:8px;padding:10px 14px;font-family:DM Sans,sans-serif;min-width:160px;">
            <div style="color:#f1f5f9;font-weight:700;font-size:13px;margin-bottom:6px;">${st.city?.replace(/_/g, ' ')}</div>
            ${st.arrival_time ? `<div style="color:#64748b;font-size:11px;">পৌঁছায়: <span style="color:#94a3b8;font-family:monospace">${st.arrival_time}</span></div>` : ''}
            ${st.departure_time ? `<div style="color:#64748b;font-size:11px;">ছাড়ে: <span style="color:#94a3b8;font-family:monospace">${st.departure_time}</span></div>` : ''}
            ${st.halt && st.halt !== '---' ? `<div style="color:#64748b;font-size:11px;">halt: <span style="color:#94a3b8">${st.halt} min</span></div>` : ''}
          </div>
        `;

                marker.bindPopup(popupContent, {
                    className: 'dark-popup',
                    maxWidth: 220,
                });
            });

            // Fit map to route
            const bounds = L.latLngBounds(latlngs);
            map.fitBounds(bounds, { padding: [40, 40] });
        };
        document.head.appendChild(script);

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [stations]);

    return <div ref={mapRef} style={{ width: '100%', height: '100%', minHeight: 500 }} />;
}

export default function TrainRoute() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const train = state?.train;

    if (!train) {
        return (
            <div style={{ ...s.page, alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ color: '#475569', fontSize: 14 }}>ট্রেনের তথ্য পাওয়া যায়নি।</div>
                <button style={{ ...s.backBtn, marginTop: 16 }} onClick={() => navigate(-1)}>← ফিরে যান</button>
            </div>
        );
    }

    const typeColor = TYPE_COLORS[train.type] || TYPE_COLORS.Ml;
    const hasRouteDetails = !!train.route_details?.routes;
    const routes = train.route_details?.routes || [];

    // Build stations for map: use route_details if available, else depart+arrival only
    const mapStations = hasRouteDetails
        ? routes.filter(r => r.lat && r.lng)
        : [
            { city: train.departStation, lat: train.depart_lat, lng: train.depart_lng, departure_time: train.departTime },
            { city: train.arrivalStation, lat: train.arrival_lat, lng: train.arrival_lng, arrival_time: train.arrivalTime },
        ].filter(s => s.lat && s.lng);

    return (
        <div style={s.page}>
            <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&family=DM+Mono:wght@400;600&display=swap" rel="stylesheet" />
            <style>{`.dark-popup .leaflet-popup-content-wrapper,.dark-popup .leaflet-popup-tip{background:transparent;box-shadow:none;padding:0}`}</style>

            {/* Topbar */}
            <div style={s.topbar}>
                <button style={s.backBtn} onClick={() => navigate(-1)}>← ফিরে যান</button>
                <div style={s.trainInfo}>
                    <div style={s.trainTitle}>#{train.trainID} — {train.trainName}</div>
                    <div style={s.trainMeta}>{train.departStation} → {train.arrivalStation} · {train.departTime} – {train.arrivalTime}</div>
                </div>
                <div style={{ ...s.badge, background: typeColor.bg, color: typeColor.text, borderColor: typeColor.border }}>
                    {TYPE_LABELS[train.type] || train.type}
                </div>
            </div>

            {/* Info strip */}
            <div style={s.infoStrip}>
                {train.offDay && train.offDay !== 'N/A' && (
                    <div style={s.infoItem}>🚫 বন্ধ: <span style={s.infoVal}>{train.offDay}</span></div>
                )}
                {train.route_details?.total_duration && (
                    <div style={s.infoItem}>⏱ মোট সময়: <span style={s.infoVal}>{train.route_details.total_duration}</span></div>
                )}
                {hasRouteDetails && (
                    <div style={s.infoItem}>🛑 স্টেশন: <span style={s.infoVal}>{routes.length} টি</span></div>
                )}
                {train.route_details?.days && (
                    <div style={s.infoItem}>📅 চলে: <span style={s.infoVal}>{train.route_details.days.join(', ')}</span></div>
                )}
            </div>

            {/* Main body */}
            <div style={{ ...s.body, flex: 1 }}>

                {/* Sidebar */}
                <div style={s.sidebar}>
                    <div style={s.sidebarHeader}>
                        <div style={s.sidebarTitle}>
                            {hasRouteDetails ? `স্টেশন তালিকা (${routes.length} টি)` : 'রুট তথ্য'}
                        </div>
                    </div>

                    {hasRouteDetails ? (
                        <StationList stations={routes} />
                    ) : (
                        <div style={s.simpleRoute}>
                            <div style={s.simpleCard}>
                                <div style={s.simpleStation}>
                                    <div style={s.simpleStationLabel}>ছাড়ে</div>
                                    <div style={s.simpleStationName}>{train.departStation}</div>
                                    <div style={s.simpleTime}>{train.departTime}</div>
                                </div>
                                <div style={s.simpleLine}>
                                    <div style={s.simpleArrow}>→</div>
                                    <div style={s.simpleDuration}>direct</div>
                                </div>
                                <div style={s.simpleStation}>
                                    <div style={s.simpleStationLabel}>পৌঁছায়</div>
                                    <div style={s.simpleStationName}>{train.arrivalStation}</div>
                                    <div style={s.simpleTime}>{train.arrivalTime}</div>
                                </div>
                            </div>
                            <div style={{ color: '#475569', fontSize: 12, marginTop: 16, textAlign: 'center' }}>
                                এই ট্রেনের বিস্তারিত route তথ্য নেই।<br />শুধু শুরু ও শেষ স্টেশন দেখানো হচ্ছে।
                            </div>
                        </div>
                    )}
                </div>

                {/* Map */}
                <div style={s.mapArea}>
                    {mapStations.length >= 2 ? (
                        <OSMMap stations={mapStations} />
                    ) : (
                        <div style={s.noRoute}>
                            <div style={s.noRouteIcon}>🗺️</div>
                            <div style={s.noRouteText}>Map দেখানো সম্ভব হচ্ছে না</div>
                            <div style={s.noRouteSub}>স্টেশনের GPS তথ্য পাওয়া যায়নি</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}